import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useGoBack from "hooks/useGoBack";
import { searchPosts, togglePostLike } from "api/community";
import { useSnackbar } from "contexts/SnackbarContext";
import PostContentPreview from "components/community/PostContentPreview";
import useUserStore from "@store/useUserStore";
import MediaCarousel from "components/community/MediaCarousel";
import SubHeader from "components/common/SubHeader";
import useCommunityCategories from "hooks/useCommunityCategories";
import useRefreshCallback from "hooks/useRefreshCallback";
import { formatTime } from "utils/util";
import { getPublicUserKey } from "utils/userIdentity";
import "pages/community/community.scss";

const CommunitySearch = () => {
    const { categoryMap } = useCommunityCategories();
    const navigate = useNavigate();
    const goBack = useGoBack();
    const { showError } = useSnackbar();
    const userInfo = useUserStore((state) => state.userInfo);
    const [keyword, setKeyword] = useState("");
    const [activeKeyword, setActiveKeyword] = useState("");
    const [posts, setPosts] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const inputRef = useRef(null);
    const observerRef = useRef(null);
    const bottomRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const loadResults = useCallback(
        async (query, pageNum, append = false) => {
            try {
                if (append) {
                    setIsLoadingMore(true);
                } else {
                    setIsLoading(true);
                    setPosts([]);
                }
                const result = await searchPosts(query, pageNum);
                if (result.success) {
                    setPosts((prev) =>
                        append ? [...prev, ...result.data] : result.data
                    );
                    setTotalPages(result.meta?.totalPages || 0);
                }
            } catch (error) {
                console.error("검색 실패:", error);
                showError("검색에 실패했습니다.");
            } finally {
                setIsLoading(false);
                setIsLoadingMore(false);
            }
        },
        [showError]
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        const trimmed = keyword.trim();
        if (trimmed.length < 2) {
            showError("2자 이상 입력해주세요.");
            return;
        }
        setPage(1);
        setHasSearched(true);
        setActiveKeyword(trimmed);
        loadResults(trimmed, 1);
    };

    // Pull-to-Refresh: 현재 검색어 기준으로 결과 재조회
    useRefreshCallback(() => {
        if (!activeKeyword) return;
        setPage(1);
        return loadResults(activeKeyword, 1);
    });

    // 무한 스크롤
    useEffect(() => {
        if (!bottomRef.current || !activeKeyword) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isLoadingMore && page < totalPages) {
                    const nextPage = page + 1;
                    setPage(nextPage);
                    loadResults(activeKeyword, nextPage, true);
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(bottomRef.current);
        observerRef.current = observer;
        return () => observer.disconnect();
    }, [page, totalPages, isLoadingMore, loadResults, activeKeyword]);

    // 좋아요 토글
    const handleLikeToggle = useCallback(
        async (e, postId) => {
            e.stopPropagation();
            if (!userInfo) {
                navigate("/login");
                return;
            }
            setPosts((prev) =>
                prev.map((p) =>
                    p.id === postId
                        ? {
                              ...p,
                              is_liked: !p.is_liked,
                              like_count: p.is_liked ? p.like_count - 1 : p.like_count + 1,
                          }
                        : p
                )
            );
            try {
                await togglePostLike(postId);
            } catch {
                setPosts((prev) =>
                    prev.map((p) =>
                        p.id === postId
                            ? {
                                  ...p,
                                  is_liked: !p.is_liked,
                                  like_count: p.is_liked ? p.like_count - 1 : p.like_count + 1,
                              }
                            : p
                    )
                );
            }
        },
        [userInfo, navigate]
    );

    return (
        <div className="community-page community-list community-search">
            <SubHeader className="has-search" customGoback={() => goBack()}>
                <form className="search-bar" onSubmit={handleSubmit}>
                    <input
                        ref={inputRef}
                        className="search-bar__input"
                        type="text"
                        placeholder="게시글 검색"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleSubmit(e);
                            }
                        }}
                    />
                    {keyword && (
                        <button
                            type="button"
                            className="search-bar__clear"
                            onClick={() => {
                                setKeyword("");
                                inputRef.current?.focus();
                            }}
                        >
                            <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                                <circle cx="10" cy="10" r="8" fill="#d1d5db" />
                                <path d="M7 7l6 6M13 7l-6 6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        </button>
                    )}
                </form>
            </SubHeader>

            {!hasSearched ? (
                <div className="search-guide">
                    <p className="search-guide__text">게시글 내용을 검색해보세요</p>
                </div>
            ) : isLoading ? (
                <div className="search-loading">검색 중...</div>
            ) : posts.length === 0 ? (
                <div className="empty-state">
                    <h3 className="empty-title">검색 결과가 없어요</h3>
                    <p className="empty-description">
                        "{activeKeyword}"에 대한 게시글을 찾을 수 없습니다.
                    </p>
                </div>
            ) : (
                <div className="post-list">
                    {posts.map((post) => (
                        <article
                            key={post.id}
                            className="post-card"
                            onClick={() => window.open(`/community/${post.id}`, "_blank")}
                        >
                            <div className="author-row" onClick={(e) => {
                                e.stopPropagation();
                                if (getPublicUserKey(post)) window.open(`/community/user/${encodeURIComponent(getPublicUserKey(post))}`, "_blank");
                            }}>
                                {post.author_profile_image ? (
                                    <img src={post.author_profile_image} alt="" className="author-avatar" />
                                ) : (
                                    <div className="author-avatar-placeholder">
                                        {(post.author_nickname || post.author_name || "?").charAt(0)}
                                    </div>
                                )}
                                <div className="author-info">
                                    <div className="author-name-row">
                                        <span className="author-nickname">
                                            {post.author_nickname || post.author_name}
                                        </span>
                                        {post.post_category && (
                                            <>
                                                <span className="separator">›</span>
                                                <span className="activity-type">
                                                    {categoryMap[post.post_category] || post.post_category}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    <span className="post-time">{formatTime(post.created_at)}</span>
                                </div>
                            </div>

                            <PostContentPreview content={post.content} />

                            <MediaCarousel mediaUrls={post.media_urls} />

                            <div className="action-bar">
                                <button className="action-item" onClick={(e) => handleLikeToggle(e, post.id)}>
                                    <svg className="action-icon" viewBox="0 0 20 20" fill="none">
                                        <path
                                            d="M10 17.5s-7-4.5-7-9.5A4 4 0 0 1 10 5a4 4 0 0 1 7 3c0 5-7 9.5-7 9.5z"
                                            fill={post.is_liked ? "#e40000" : "none"}
                                            stroke={post.is_liked ? "#e40000" : "#464a4d"}
                                            strokeWidth="1.5"
                                        />
                                    </svg>
                                    {post.like_count > 0 && <span className="action-count">{post.like_count}</span>}
                                </button>
                                <button
                                    className="action-item"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(`/community/${post.id}`, "_blank");
                                    }}
                                >
                                    <svg className="action-icon" viewBox="0 0 20 20" fill="none">
                                        <path
                                            d="M17 10c0 3.866-3.134 7-7 7a7.2 7.2 0 0 1-3.5-.9L3 17l.9-3.5A7.2 7.2 0 0 1 3 10c0-3.866 3.134-7 7-7s7 3.134 7 7z"
                                            stroke="#464a4d"
                                            strokeWidth="1.5"
                                            fill="none"
                                        />
                                    </svg>
                                    {post.comment_count > 0 && <span className="action-count">{post.comment_count}</span>}
                                </button>
                            </div>
                        </article>
                    ))}
                    <div ref={bottomRef} />
                    {isLoadingMore && <div className="loading-more">로딩 중...</div>}
                </div>
            )}
        </div>
    );
};

export default CommunitySearch;
